import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { Loader2, Printer } from "lucide-react";
import { printClosing } from "@/lib/printShift";
import { useSupervisorApproval } from "@/components/SupervisorApprovalDialog";
import { prepareDurableClose, closeDurableShift, cancelDurableClose } from '@/lib/shift-journal';

export default function ShiftCloseModal({ open, onClose, onClosed, storeName, journalScope }) {
  const { approveAction, approvalDialog } = useSupervisorApproval();
  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState(null);
  const [summary, setSummary] = useState(null);
  const [cashCounted, setCashCounted] = useState("");
  const [reason, setReason] = useState("");
  const [varianceLimit, setVarianceLimit] = useState(20000);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const dismiss = async () => {
    if (saving) return;
    try { await cancelDurableClose(journalScope); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true); setCashCounted(""); setReason(""); setShift(null); setSummary(null);
    let active = true;
    prepareDurableClose(api, journalScope).then(() => Promise.all([api.get("/shifts/summary"), api.get("/supervisor/settings")]))
      .then(([shiftResponse, securityResponse]) => {
        if (!active) return;
        setShift(shiftResponse.data.shift); setSummary(shiftResponse.data.summary);
        setVarianceLimit(Number(securityResponse.data.cash_variance_limit || 0));
      })
      .catch((error) => { if (active) { toast.error(apiError(error)); cancelDurableClose(journalScope).catch(() => {}); onClose(); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // Parent callback identities change during sync; reopening resets the form, not each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, journalScope]);

  const cash = Number(cashCounted);
  const validCash = cashCounted !== "" && Number.isSafeInteger(cash) && cash >= 0;
  const expected = Number(summary?.cash_expected || 0);
  const difference = cash - expected;
  const doClose = async () => {
    if (submitting.current || loading || !summary || !shift) return;
    if (!validCash) return toast.error("Isi total kas yang dihitung. Masukkan 0 jika tidak ada uang tunai.");
    if (Math.abs(difference) > 0.009 && !reason.trim()) return toast.error("Isi alasan selisih kas");
    submitting.current = true; setSaving(true);
    try {
      let approval = {};
      if (Math.abs(difference) > varianceLimit) {
        approval = await approveAction("close_shift_variance", `Selisih ${rupiah(difference)} · kas fisik ${rupiah(cash)}`);
        if (approval === false) return;
      }
      const data = await closeDurableShift(api, journalScope, shift, { cash_counted: cash, denominations: [], variance_reason: reason.trim() || null }, approval);
      toast.success("Shift ditutup"); printClosing(data, data.summary, storeName); onClosed(data);
    } catch (error) { toast.error(apiError(error)); }
    finally { submitting.current = false; setSaving(false); }
  };

  return <>
    {approvalDialog}
    <Dialog open={open} onOpenChange={value => { if (!value) dismiss(); }}>
      <DialogContent data-testid="shift-close-dialog" aria-describedby={undefined} className="bg-ui-card border-ui-line text-ui-text max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tutup Shift</DialogTitle></DialogHeader>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-readable" /></div> : summary && <div className="space-y-3">
          <p className="text-xs text-ui-muted">Tutup sif hanya selesai saat online, antrean transaksi kosong, dan kiriman Akun Kas selesai. Penutupan tidak mengirim setoran kas kedua.</p>
          <Button variant="outline" disabled={saving} onClick={async () => { setSaving(true); try { await api.post('/akunkas/sync-failed', {}); toast.success('Pemeriksaan kiriman Akun Kas selesai. Coba tutup sif.'); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); } }}>Sinkronkan Akun Kas</Button>
          <div className="rounded-lg border border-ui-line bg-ui-soft p-3">
            <p className="text-sm text-ui-muted">Total Kas Seharusnya</p>
            <p data-testid="shift-cash-expected" className="mt-1 text-2xl font-bold font-mono break-all">{rupiah(expected)}</p>
          </div>
          <label className="block text-sm font-semibold">Total Kas Dihitung
            <MoneyInput data-testid="shift-cash-input" value={cashCounted} onValueChange={setCashCounted} disabled={saving} placeholder="Masukkan total uang tunai" className="mt-1 h-11 text-right font-mono text-lg" />
          </label>
          <p className="text-xs text-ui-muted">Isi total uang tunai yang benar-benar dihitung. Masukkan 0 jika tidak ada uang tunai.</p>
          {validCash && <>
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${difference === 0 ? "bg-brand-soft text-brand-readable" : "bg-danger-soft text-danger-readable"}`}><b>Selisih</b><b data-testid="shift-selisih" className="font-mono">{rupiah(difference)}</b></div>
            {Math.abs(difference) > 0.009 && <label className="block text-xs font-semibold">Alasan Selisih<Input data-testid="shift-variance-reason" value={reason} disabled={saving} onChange={event => setReason(event.target.value)} maxLength={500} placeholder="Contoh: salah memberi kembalian" className="mt-1" /></label>}
            {Math.abs(difference) > varianceLimit && <p className="text-xs text-warning-readable">Selisih melewati batas {rupiah(varianceLimit)}. PIN Supervisor akan diminta saat menutup.</p>}
          </>}
        </div>}
        <DialogFooter><Button variant="outline" disabled={saving} onClick={dismiss}>Batal</Button><Button data-testid="shift-close-confirm" onClick={doClose} disabled={saving || loading || !summary || !validCash} className="bg-danger hover:bg-danger text-ui-text gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}Tutup & Cetak</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
